import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  checkGrievanceMobile,
  getGrievanceMobileErrorMessage,
  createGrievanceUser as createGrievanceUserRequest,
  getGrievanceUserErrorMessage,
  submitGrievance as submitGrievanceRequest,
  getGrievanceSubmitErrorMessage,
  generateGrievanceOtp as generateGrievanceOtpRequest,
  getGenerateOtpErrorMessage,
  verifyGrievanceOtp as verifyGrievanceOtpRequest,
  getVerifyOtpErrorMessage,
} from "../../services/grievanceService";

const initialState = {
  verifiedMobile: "",
  userId: null,
  status: "idle",
  error: "",
  createUserStatus: "idle",
  createUserError: "",
  submitStatus: "idle",
  submitError: "",
};

export const verifyGrievanceMobile = createAsyncThunk(
  "grievance/verifyMobile",
  async (mobileNumber, { rejectWithValue }) => {
    try {
      return await checkGrievanceMobile(mobileNumber);
    } catch (error) {
      return rejectWithValue(getGrievanceMobileErrorMessage(error));
    }
  }
);

export const generateGrievanceOtp = createAsyncThunk(
  "grievance/generateOtp",
  async (payload, { rejectWithValue }) => {
    try {
      return await generateGrievanceOtpRequest(payload);
    } catch (error) {
      return rejectWithValue(getGenerateOtpErrorMessage(error));
    }
  }
);

export const verifyGrievanceOtp = createAsyncThunk(
  "grievance/verifyOtp",
  async (payload, { rejectWithValue }) => {
    try {
      return await verifyGrievanceOtpRequest(payload);
    } catch (error) {
      return rejectWithValue(getVerifyOtpErrorMessage(error));
    }
  }
);

// Called only after OTP verification succeeds for a mobile number that was not found.
export const createGrievanceUser = createAsyncThunk(
  "grievance/createUser",
  async ({ userName, mobileNo }, { rejectWithValue }) => {
    try {
      return await createGrievanceUserRequest({ userName, mobileNo });
    } catch (error) {
      return rejectWithValue(getGrievanceUserErrorMessage(error));
    }
  }
);

// Called on final "Submit Grievance", using the userId from mobile-check/create-user.
export const submitGrievance = createAsyncThunk(
  "grievance/submit",
  async (payload, { rejectWithValue }) => {
    try {
      return await submitGrievanceRequest(payload);
    } catch (error) {
      return rejectWithValue(getGrievanceSubmitErrorMessage(error));
    }
  }
);

const grievanceSlice = createSlice({
  name: "grievance",
  initialState,
  reducers: {
    clearGrievanceMobileVerification: (state) => {
      state.verifiedMobile = "";
      state.userId = null;
      state.status = "idle";
      state.error = "";
      state.createUserStatus = "idle";
      state.createUserError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyGrievanceMobile.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(verifyGrievanceMobile.fulfilled, (state, action) => {
        state.verifiedMobile = action.payload.mobileNumber;
        state.userId = action.payload.userId;
        state.status = "succeeded";
      })
      .addCase(verifyGrievanceMobile.rejected, (state, action) => {
        state.verifiedMobile = "";
        state.userId = null;
        state.status = "failed";
        state.error = action.payload || "Unable to verify this mobile number.";
      })
      .addCase(createGrievanceUser.pending, (state) => {
        state.createUserStatus = "loading";
        state.createUserError = "";
      })
      .addCase(createGrievanceUser.fulfilled, (state, action) => {
        state.createUserStatus = "succeeded";
        state.userId = action.payload?.userId ?? state.userId;
      })
      .addCase(createGrievanceUser.rejected, (state, action) => {
        state.createUserStatus = "failed";
        state.createUserError = action.payload || "Unable to create grievance user.";
      })
      .addCase(submitGrievance.pending, (state) => {
        state.submitStatus = "loading";
        state.submitError = "";
      })
      .addCase(submitGrievance.fulfilled, (state) => {
        state.submitStatus = "succeeded";
      })
      .addCase(submitGrievance.rejected, (state, action) => {
        state.submitStatus = "failed";
        state.submitError = action.payload || "Unable to submit grievance.";
      });
  },
});

export const { clearGrievanceMobileVerification } = grievanceSlice.actions;
export default grievanceSlice.reducer;
